import { STAGE_LABELS } from "../../utils/stages.js";
import { isClosedCandidateStage } from "@/shared/candidate-stages.js";

interface CandidateProgressProps {
  stage: string;
  hasAssignment: boolean;
  previousStage?: string | null;
  completedStages?: string[];
}

const PIPELINE_STAGES = [
  "applied",
  "assignment",
  "review",
  "interview",
  "interview_complete",
  "offer_pending",
  "offer",
];

export function CandidateProgress({
  stage,
  hasAssignment,
  previousStage,
  completedStages = [],
}: CandidateProgressProps) {
  const isTerminal = isClosedCandidateStage(stage);
  const progressStage = isTerminal && previousStage ? previousStage : stage;
  const showAssignmentPath =
    hasAssignment || progressStage === "assignment" || progressStage === "review";
  const stages = showAssignmentPath
    ? PIPELINE_STAGES
    : PIPELINE_STAGES.filter((candidateStage) => {
        return candidateStage !== "assignment" && candidateStage !== "review";
      });
  const currentIndex = stages.indexOf(progressStage);
  const currentLabel = STAGE_LABELS[stage] ?? stage;
  const statusColor =
    stage === "rejected"
      ? "bg-red-100 text-red-800"
      : stage === "withdrawn"
        ? "bg-gray-200 text-gray-700"
        : stage === "talent_pool"
          ? "bg-amber-100 text-amber-800"
          : "bg-primary-100 text-primary-800";

  return (
    <section
      className="rounded-lg border border-gray-200 bg-white p-4"
      aria-label={`Candidate stage: ${currentLabel}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Current stage
          </p>
          <p className="mt-0.5 text-xl font-semibold text-gray-900">{currentLabel}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-sm font-semibold ${statusColor}`}>
          {currentLabel}
        </span>
      </div>

      <div className="mt-4 overflow-x-auto pb-1">
        <div className="flex min-w-[36rem] items-start">
          {stages.map((pipelineStage, index) => {
            const precedesCurrent =
              currentIndex >= 0 && (isTerminal ? index <= currentIndex : index < currentIndex);
            const isCompleted =
              precedesCurrent &&
              (pipelineStage !== "interview_complete" ||
                completedStages.includes("interview_complete") ||
                progressStage === "interview_complete");
            const isCurrent = !isTerminal && index === currentIndex;
            const nextPipelineStage = stages[index + 1];
            const nextPrecedesCurrent =
              currentIndex >= 0 &&
              (isTerminal ? index + 1 <= currentIndex : index + 1 < currentIndex);
            const isNextCompleted =
              nextPrecedesCurrent &&
              (nextPipelineStage !== "interview_complete" ||
                completedStages.includes("interview_complete") ||
                progressStage === "interview_complete");
            const isConnectorCompleted =
              isCompleted && (isNextCompleted || (!isTerminal && index + 1 === currentIndex));

            return (
              <div key={pipelineStage} className="flex flex-1 items-start">
                <div className="flex flex-1 flex-col items-center text-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                      isCompleted
                        ? "bg-green-500 text-white"
                        : isCurrent
                          ? "bg-primary text-primary-foreground ring-2 ring-primary-200"
                          : "bg-gray-200 text-gray-500"
                    }`}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    {isCompleted ? (
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={`mt-1 text-[11px] font-medium ${
                      isCurrent
                        ? "text-primary-700"
                        : isCompleted
                          ? "text-green-700"
                          : "text-gray-400"
                    }`}
                  >
                    {STAGE_LABELS[pipelineStage] ?? pipelineStage}
                  </span>
                </div>
                {index < stages.length - 1 && (
                  <div
                    className={`mt-4 h-0.5 flex-1 ${isConnectorCompleted ? "bg-green-500" : "bg-gray-200"}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
